import { Alert, Button } from '@design-system';
import { renderShopifyArtistName } from '@utils/artists';
import { storyblokProductReleaseType } from '@utils/storyblokReleaseType';
import { useTranslation } from 'next-i18next';
import { FC } from 'react';

import { getProductStatus } from '../../../helpers/purchasing';
import { useHubspotChat } from '../../../hooks/useHubspotChat';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../types/Purchasing';
import { AnalyticsObjectProvider } from '../../../utils/analytics/object';
import { FramePicker } from './FramePicker';

type Props<T extends ProductPurchaseDetails> = {
  onCloseDrawer: () => void;
  onSubmit: () => void;
  product: ProductType;
  purchasingController: ProductPurchasingController<T>;
};

export const ReviewFrameForm: FC<Props<ProductPurchaseDetails>> = ({
  onCloseDrawer,
  onSubmit,
  product,
  purchasingController,
}) => {
  const { openHandler: openHubspotChat } = useHubspotChat();
  const { t } = useTranslation(['product']);

  return (
    <AnalyticsObjectProvider
      name="product page product selector"
      properties={{
        'product_title': product.title,
        'artist name': renderShopifyArtistName(
          purchasingController.product?.artist ?? '',
        ),
        'product_release_type': storyblokProductReleaseType(product),
        'product_release_date': product.launchDate,
        'product_frame_chosen': Boolean(purchasingController.frame),
        'product_status': getProductStatus(product, purchasingController),
        'product_medium': product?.medium,
        'product_price': purchasingController.product?.price,
        'product_edition_size': product?.editionSize,
        'product_id': product?.id,
        'product_can_be_framed': Boolean(
          (purchasingController.frameOptions?.length ?? 0) > 0,
        ),
        'product_frame_upgrade_available': Boolean(
          purchasingController.frameOptions,
        ),
        'product_frame_glazing': purchasingController.frame?.variant.title,
        ecommerce: {
          items: [
            {
              item_id: product?.id,
              item_name: product.title,
              item_brand: renderShopifyArtistName(
                purchasingController.product?.artist ?? '',
              ),
              item_category: product.medium?.join(','),
              price: purchasingController.product?.price,
              quantity: purchasingController.quantity,
            },
          ],
        },
      }}
    >
      {() =>
        purchasingController.frameOptions?.length ? (
          <FramePicker
            description={
              product.details.length ? product.details[0].framingOptions : ''
            }
            frameOptoins={purchasingController.frameOptions}
            frameSelected={purchasingController.frame}
            hideSubmitButton={false}
            hideTitle={false}
            includeUnframed
            onGetInTouchClick={() => {
              onCloseDrawer();
              openHubspotChat();
            }}
            onSubmit={(frame) => {
              purchasingController.setFrame(frame);
              onSubmit();
            }}
            submitButtonLabel={t('drawers.review.framing.cta')}
          />
        ) : (
          <div className="flex flex-col gap-9">
            <div className="mb-6 hidden w-full flex-1 md:flex">
              <p className="text-md font-bold md:text-lg">
                {t('drawers.review.framing.title')}
              </p>
            </div>
            <Alert status="error">
              No frames found for the selected works.
            </Alert>

            <Button
              fullWidth
              label="Continue"
              onClick={onSubmit}
              variant="primary"
            />
          </div>
        )
      }
    </AnalyticsObjectProvider>
  );
};
